import { forwardRef, useState } from "react";
import PrivacyPolicy from "./PrivacyPolicy";
import "./Contact.css";

interface ContactProps {
  id: string;
}

interface FieldErrors {
  name?: string;
  email?: string;
  message?: string;
  consent?: string;
}

// Baked in at build time via the VITE_CONTACT_API_URL environment variable
// (set it in the Cloudflare dashboard under Workers Builds → Settings → Variables).
// Falls back to the deployed contact-form worker URL.
const CONTACT_API_URL =
  import.meta.env.VITE_CONTACT_API_URL ??
  "https://catarina-contact-form.carl-hidestal.workers.dev/api/contact";

type FormStatus = "idle" | "submitting" | "succeeded";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Contact = forwardRef<HTMLDivElement, ContactProps>(({ id }, ref) => {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(false);

    const data = new FormData(e.currentTarget);
    const name = String(data.get("name") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();
    const phone = String(data.get("phone") ?? "").trim();
    const subject = String(data.get("subject") ?? "");
    const message = String(data.get("message") ?? "").trim();
    const consent = data.get("consent") === "on";
    // Honeypot field — bots fill it, humans never see it.
    const company = String(data.get("company") ?? "").trim();

    const nextErrors: FieldErrors = {};
    if (!name) nextErrors.name = "Namn är obligatoriskt";
    if (!email) nextErrors.email = "E-post är obligatoriskt";
    else if (!EMAIL_RE.test(email))
      nextErrors.email = "Ange en giltig e-postadress";
    if (!consent)
      nextErrors.consent = "Du måste godkänna hanteringen av dina uppgifter";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setStatus("submitting");
    try {
      const response = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          subject,
          message,
          consent,
          company,
        }),
      });
      if (!response.ok)
        throw new Error(`contact API responded ${response.status}`);
      setStatus("succeeded");
    } catch {
      setStatus("idle");
      setFormError(true);
    }
  };

  return (
    <section
      ref={ref}
      id={id}
      className="contact section"
      aria-labelledby="contact-title"
    >
      <div className="container">
        <header className="section__header animate-in">
          <h2 id="contact-title" className="section__title">
            <span className="text-terracotta">Kontakta</span> mig
          </h2>
          <p className="section__subtitle">
            Om du har frågor om min verksamhet eller vill ha mer information når
            du mig enkelt via telefon, mejl eller formuläret nedan.
          </p>
        </header>

        <div className="contact__grid grid grid--2">
          <div className="contact__map animate-in" aria-hidden="true">
            <div className="contact__map-wrapper">
              <iframe
                title="Karta över Catarinas praktik på Spångatan 20 A, Malmö"
                src="https://www.google.com/maps?q=Sp%C3%A5ngatan+20A,+211+53+Malm%C3%B6&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="contact__map-iframe"
              ></iframe>
              <div className="contact__map-overlay">
                <address className="contact__map-address">
                  <strong>Catarina Malmberg</strong>
                  <br />
                  Terapi- och Friskvårdscentrum (Utsikt)
                  <br />
                  Spångatan 20 A<br />
                  211 53 Malmö
                  <br />
                  Tel: 0733-788272
                </address>
              </div>
            </div>
          </div>

          <div className="contact__content animate-in animate-delay-1">
            {status === "succeeded" ? (
              <div
                className="card contact__form form__success"
                role="status"
                aria-live="polite"
              >
                <svg
                  className="form__success-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h3>Tack för ditt meddelande!</h3>
                <p>Jag återkommer så snart jag kan.</p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="card contact__form"
                noValidate
              >
                <div className="form__group">
                  <label htmlFor="name" className="form__label">
                    Ditt namn{" "}
                    <span className="form__required" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={`form__input ${errors.name ? "form__input--error" : ""}`}
                    required
                    aria-describedby={errors.name ? "name-error" : undefined}
                    aria-invalid={errors.name ? "true" : "false"}
                    disabled={status === "submitting"}
                  />
                  {errors.name && (
                    <p id="name-error" className="form__error" role="alert">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div className="form__group">
                  <label htmlFor="email" className="form__label">
                    E-post{" "}
                    <span className="form__required" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className={`form__input ${errors.email ? "form__input--error" : ""}`}
                    required
                    aria-describedby={errors.email ? "email-error" : undefined}
                    aria-invalid={errors.email ? "true" : "false"}
                    disabled={status === "submitting"}
                  />
                  {errors.email && (
                    <p id="email-error" className="form__error" role="alert">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="form__group">
                  <label htmlFor="phone" className="form__label">
                    Telefonnummer
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form__input"
                    placeholder="07X-XXX XX XX"
                    autoComplete="tel"
                    disabled={status === "submitting"}
                  />
                </div>

                <div className="form__group">
                  <label htmlFor="subject" className="form__label">
                    Ärende
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    className="form__input form__select"
                    disabled={status === "submitting"}
                    defaultValue=""
                  >
                    <option value="">Välj ärende</option>
                    <option value="samtalsbehandling">Samtalsbehandling</option>
                    <option value="parsamtal">Parsamtal</option>
                    <option value="mindfulness">Mindfulness</option>
                    <option value="act">ACT</option>
                    <option value="other">Annat / Osäker</option>
                  </select>
                </div>

                <div className="form__group">
                  <label htmlFor="message" className="form__label">
                    Vill du berätta mer? (valfritt)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    className={`form__input form__textarea ${errors.message ? "form__input--error" : ""}`}
                    placeholder="Beskriv kort din situation och vad du hoppas få hjälp med..."
                    rows={5}
                    aria-describedby={
                      errors.message ? "message-error" : undefined
                    }
                    aria-invalid={errors.message ? "true" : "false"}
                    disabled={status === "submitting"}
                  />
                  {errors.message && (
                    <p id="message-error" className="form__error" role="alert">
                      {errors.message}
                    </p>
                  )}
                </div>

                <div className="form__group form__group--checkbox">
                  <input
                    type="checkbox"
                    id="consent"
                    name="consent"
                    className="form__checkbox"
                    required
                    aria-describedby={
                      errors.consent ? "consent-error" : undefined
                    }
                    aria-invalid={errors.consent ? "true" : "false"}
                    disabled={status === "submitting"}
                  />
                  <label
                    htmlFor="consent"
                    className="form__label form__label--checkbox"
                  >
                    Jag samtycker till att mina uppgifter lagras och hanteras
                    enligt{" "}
                    <button
                      type="button"
                      className="form__link"
                      onClick={() => setPrivacyOpen(true)}
                    >
                      integritetspolicyn
                    </button>
                    <span className="form__required" aria-hidden="true">
                      *
                    </span>
                  </label>
                  {errors.consent && (
                    <p id="consent-error" className="form__error" role="alert">
                      {errors.consent}
                    </p>
                  )}
                </div>

                <input
                  type="text"
                  name="company"
                  autoComplete="off"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="form__honeypot"
                />

                <button
                  type="submit"
                  className="btn btn--primary form__submit"
                  disabled={status === "submitting"}
                >
                  {status === "submitting" ? (
                    <>
                      <span className="btn__spinner" aria-hidden="true" />
                      Skickar...
                    </>
                  ) : (
                    "Skicka meddelande"
                  )}
                </button>

                {formError && (
                  <div className="form__error-message" role="alert">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    <p>
                      Något gick fel. Försök igen eller ring mig direkt på
                      0733-788272.
                    </p>
                  </div>
                )}
              </form>
            )}
            <PrivacyPolicy
              open={privacyOpen}
              onClose={() => setPrivacyOpen(false)}
            />
          </div>
        </div>
      </div>
    </section>
  );
});

Contact.displayName = "Contact";

export default Contact;
